import { useState } from "react";
import { addDays, todayIn, weekday, type IsoDate } from "../../../shared/dates";
import { NOTE_COLORS, visibleNotes, type FamilyNote, type NoteColor } from "../../../shared/home";
import type { Member } from "../../../shared/models";
import { useAppUi } from "../../app/uiContext";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf, useMembers } from "../../data/queries";
import { Icon } from "../../ui/Icon";
import { Confirm, Sheet } from "../../ui/Sheet";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { relativeAge, useNow } from "../../ui/time";
import { fmtDate } from "../calendar/calendarModel";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { useNoteActions, useNotes } from "./api";
import "./home.css";

interface NotesConfig {
  title: string;
}

/** A family message board: sticky notes anyone can leave for everyone or one person. */
function Notes({ config, editing, fullscreen, openFullscreen }: WidgetProps<NotesConfig>) {
  const { timeZone } = useHousehold();
  const now = useNow("minute");
  const today = todayIn(timeZone, now);
  const q = useNotes();
  const members = useMembers().data ?? [];
  const byId = new Map(members.map((m) => [m.id, m]));
  const state = dataStateOf(q, 15 * 60_000);
  const [open, setOpen] = useState<FamilyNote | "new" | null>(null);
  const notes = visibleNotes(q.data ?? [], today);

  return (
    <div className="home-w notes">
      <WidgetHeader
        title={config.title || "Family notes"}
        meta={notes.length ? `${notes.length} ${notes.length === 1 ? "note" : "notes"}` : undefined}
        onTitle={fullscreen ? undefined : openFullscreen}
        actions={
          <button className="icon-btn icon-btn-accent" onClick={() => setOpen("new")} aria-label="Add a note" disabled={editing}>
            <Icon name="plus" />
          </button>
        }
      />
      {!q.data ? (
        state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="note" title="Notes unavailable" detail={q.error?.message} />
      ) : notes.length === 0 ? (
        <EmptyState
          icon="note"
          title="No notes yet"
          detail="Leave a message for the family."
          action={
            <button className="btn btn-sm btn-primary" onClick={() => setOpen("new")} disabled={editing}>
              Write a note
            </button>
          }
        />
      ) : (
        <ul className="note-board">
          {notes.map((n, i) => {
            const author = n.authorId ? byId.get(n.authorId) : undefined;
            const to = n.forMemberId ? byId.get(n.forMemberId) : undefined;
            return (
              <li key={n.id} className={`note note-${n.color}`} style={{ ["--tilt" as string]: `${((i * 37) % 5) - 2}deg` }}>
                <button onClick={() => setOpen(n)} disabled={editing}>
                  {to && (
                    <span className="note-to" style={{ ["--member" as string]: to.color }}>
                      For {to.displayName}
                    </span>
                  )}
                  <span className="note-text">{n.text}</span>
                  <span className="note-by">
                    {author ? `— ${author.displayName}` : ""} · {relativeAge(n.createdAt, now)}
                    {n.expiresOn && ` · until ${n.expiresOn === today ? "tonight" : fmtDate(n.expiresOn, { weekday: "short" })}`}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
      {open && <NoteEditor note={open === "new" ? null : open} members={members} today={today} onClose={() => setOpen(null)} />}
    </div>
  );
}

function NoteEditor({ note, members, today, onClose }: { note: FamilyNote | null; members: Member[]; today: IsoDate; onClose: () => void }) {
  const actions = useNoteActions();
  const { toast } = useAppUi();
  const [text, setText] = useState(note?.text ?? "");
  const [color, setColor] = useState<NoteColor>(note?.color ?? "yellow");
  const [forId, setForId] = useState<string | null>(note?.forMemberId ?? null);
  const [until, setUntil] = useState<IsoDate | null>(note?.expiresOn ?? null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const endOfWeek = addDays(today, 6 - weekday(today));
  const untilChoices: { label: string; value: IsoDate | null }[] = [
    { label: "Until removed", value: null },
    { label: "Today", value: today },
    { label: "Tomorrow", value: addDays(today, 1) },
    { label: "This week", value: endOfWeek },
  ];

  const save = () =>
    actions.save.mutate({ id: note?.id ?? null, input: { text, color, forMemberId: forId, expiresOn: until } }, { onSuccess: onClose, onError: (e) => setError((e as Error).message) });

  return (
    <Sheet
      title={note ? "Edit note" : "New note"}
      onClose={onClose}
      footer={
        <>
          {note && (
            <button className="btn btn-danger-ghost" onClick={() => setConfirm(true)}>
              <Icon name="trash" size="1.1rem" /> Remove
            </button>
          )}
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={actions.save.isPending || !text.trim()}>
            {actions.save.isPending ? "Saving…" : note ? "Save" : "Post note"}
          </button>
        </>
      }
    >
      <div className="form">
        <textarea className={`input note-input note-${color}`} value={text} onChange={(e) => setText(e.target.value)} maxLength={280} rows={4} placeholder="Write a note…" autoFocus />
        <div className="field">
          <span>Color</span>
          <div className="chip-row wrap">
            {NOTE_COLORS.map((c) => (
              <button key={c} className={`note-swatch note-${c} ${color === c ? "on" : ""}`} onClick={() => setColor(c)} aria-label={c} aria-pressed={color === c} />
            ))}
          </div>
        </div>
        {members.length > 0 && (
          <div className="field">
            <span>For</span>
            <div className="chip-row wrap">
              <button className={`chip ${forId === null ? "on" : ""}`} onClick={() => setForId(null)}>
                Everyone
              </button>
              {members.map((m) => (
                <button key={m.id} className={`chip ${forId === m.id ? "on" : ""}`} style={{ ["--member" as string]: m.color }} onClick={() => setForId(m.id)}>
                  <i className="dot" />
                  {m.displayName}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="field">
          <span>Keep it up</span>
          <div className="chip-row wrap">
            {untilChoices.map((u) => (
              <button key={u.label} className={`chip ${until === u.value ? "on" : ""}`} onClick={() => setUntil(u.value)}>
                {u.label}
              </button>
            ))}
            <input className="input input-date" type="date" value={until ?? ""} min={today} onChange={(e) => setUntil(e.target.value || null)} aria-label="Keep until" />
          </div>
        </div>
        {error && <p className="error-text">{error}</p>}
      </div>
      {confirm && note && (
        <Confirm
          title="Remove note?"
          message="It comes off the board for everyone."
          confirmLabel="Remove"
          danger
          onCancel={() => setConfirm(false)}
          onConfirm={() => {
            onClose();
            actions.remove.mutate(note, {
              onSuccess: () => toast("Note removed", { label: "Undo", run: () => actions.save.mutate({ id: null, input: { text: note.text, color: note.color, forMemberId: note.forMemberId, expiresOn: note.expiresOn } }) }),
              onError: (e) => toast(`Couldn’t remove the note: ${(e as Error).message}`),
            });
          }}
        />
      )}
    </Sheet>
  );
}

export const notesWidget: WidgetDefinition<NotesConfig> = {
  type: "family-notes",
  version: 1,
  name: "Family notes",
  description: "A message board of sticky notes: leave one for everyone or for one person, for today or until it’s removed.",
  icon: "note",
  category: "household",
  defaultSize: { w: 5, h: 4 },
  minSize: { w: 3, h: 3 },
  stackHeight: 380,
  defaults: { title: "" },
  settings: [{ key: "title", label: "Title", type: "text", maxLength: 40, placeholder: "Family notes" }],
  supportsFullscreen: true,
  Component: Notes,
};
